import classNames from 'classnames';
import { useState } from 'react';

import type { MetricValues } from '@revelio/core';
import {
  SankeyChart,
  type SankeyData,
  type SankeyLink,
  type SankeyNode,
  useChartTheme,
} from '@revelio/replots';

import { usePlotMetric } from '../../../hooks/usePlotMetric';
import type { TransitionBreakdownData } from '../../DataProvider/types';
import { useEditMode } from '../../EditModeProvider/EditModeProvider';
import { PlotFilterType } from '../../PageConfigProvider/types/item';
import { useChart } from '../ChartProvider';
import { SharableChart } from '../chart-menu/plot-share/SharableChart';
import { buildThreeTierSankeyData } from './buildThreeTierSankeyData';
import { SankeyFooter } from './SankeyFooter';
import styles from './sankey-layout.module.css';
import { isDevBuild, resolveSankeyScale } from './sankeyScale';
import { useSankeyNoticeableDifference } from './sankeyScaleDebug';

const THREE_TIER_NODE_COUNT_OPTIONS = [5, 10, 15, 20];
const DEFAULT_NODE_COUNT_OPTIONS = [10, 15, 20, 25, 30];

const THREE_TIER_LEFT_FLOW_LABEL = 'Inflow';
const THREE_TIER_RIGHT_FLOW_LABEL = 'Outflow';

const userNodeCountByChartId = new Map<string, number>();

interface BuildSankeyParams {
  data: TransitionBreakdownData[];
  useThreeTier: boolean;
  metric: MetricValues | undefined;
  effectiveNumOfNodes: number;
  color?: string;
  getPlotColor: (index: number) => string;
}

const buildSankeyData = ({
  data,
  useThreeTier,
  metric,
  effectiveNumOfNodes,
  color,
  getPlotColor,
}: BuildSankeyParams): SankeyData | null => {
  if (data.length === 0) return null;

  if (useThreeTier) {
    return buildThreeTierSankeyData({
      data,
      effectiveNumOfNodes,
      color,
      getPlotColor,
    });
  }

  const isInflow = metric === 'transitions_inflow';
  const cycleSuffix = isInflow ? '__outflow' : '__inflow';

  const primaryColorAt = (idx: number): string => {
    if (idx === 0) return color ?? getPlotColor(0);
    return getPlotColor(idx);
  };

  const primaryNodes: SankeyNode[] = [];
  const externalNodes: SankeyNode[] = [];
  const seenNodeIds = new Set<string>();
  const colorByPrimaryId = new Map<string, string>();
  const primaryIdSet = new Set<string>();

  data.forEach((entry, idx) => {
    const label = entry.dim ?? '';
    const id = entry.id ?? label;
    if (!id || seenNodeIds.has(id)) return;

    primaryNodes.push({ id, label });
    seenNodeIds.add(id);
    primaryIdSet.add(id);
    colorByPrimaryId.set(id, primaryColorAt(idx));
  });

  const externalTotalValue = new Map<string, number>();
  const links: SankeyLink[] = [];

  data.forEach((entry) => {
    const primaryId = entry.id ?? entry.dim ?? '';
    if (!primaryId) return;

    const primaryColor = colorByPrimaryId.get(primaryId) ?? primaryColorAt(0);
    const breakdownSlice = entry.breakdown?.slice(0, effectiveNumOfNodes) ?? [];

    breakdownSlice.forEach(({ dim, id, value, share }) => {
      const baseId = id ?? dim;
      const isCycle = primaryIdSet.has(baseId);
      const safeId = isCycle ? `${baseId}${cycleSuffix}` : baseId;
      const safeLabel = dim;

      if (!seenNodeIds.has(safeId)) {
        externalNodes.push({ id: safeId, label: safeLabel });
        seenNodeIds.add(safeId);
      }

      const linkValue = value ?? 0;
      externalTotalValue.set(
        safeId,
        (externalTotalValue.get(safeId) ?? 0) + linkValue
      );

      const sourceId = isInflow ? safeId : primaryId;
      const targetId = isInflow ? primaryId : safeId;

      links.push({
        source: sourceId,
        target: targetId,
        value: linkValue,
        share: share ?? 0,
        color: primaryColor,
        group: `${baseId}::${primaryId}`,
      });
    });
  });

  if (links.length === 0) return null;

  const orderedExternals = [...externalNodes].sort(
    (a, b) =>
      (externalTotalValue.get(b.id) ?? 0) - (externalTotalValue.get(a.id) ?? 0)
  );

  const tiers = isInflow
    ? [orderedExternals, primaryNodes]
    : [primaryNodes, orderedExternals];
  const nodes: SankeyNode[] = tiers.flatMap((tier, layer) =>
    tier.map((node) => ({ ...node, layer }))
  );

  return { nodes, links };
};

export const Sankey = ({ data }: { data: TransitionBreakdownData[] }) => {
  const metric = usePlotMetric();
  const { item } = useChart();
  const { getPlotColor } = useChartTheme();

  const chartId = item?.id;
  const { editMode } = useEditMode();
  const noticeableDifference = useSankeyNoticeableDifference(chartId);

  const color = item?.plot?.color;
  const showLinkValues = item?.plot?.showDataLabels ?? false;
  const scale = resolveSankeyScale(item?.plot?.sankey);
  const showOrderInversions = isDevBuild() && editMode;

  const topNFilterValue = item?.plot?.plotFilters?.find(
    (f) => f.type === PlotFilterType.TOP_N
  )?.value;
  const isFixedCount =
    typeof topNFilterValue === 'number' && topNFilterValue > 0;

  const primaryEntries = data ?? [];
  const useThreeTier = metric === 'transitions_inflows_and_outflows';

  const nodeCountOptions = useThreeTier
    ? THREE_TIER_NODE_COUNT_OPTIONS
    : DEFAULT_NODE_COUNT_OPTIONS;
  const autoNodeCount = useThreeTier && primaryEntries.length > 2 ? 5 : 10;
  const defaultNodeCount =
    isFixedCount && topNFilterValue != null ? topNFilterValue : autoNodeCount;
  const [userNodeCount, setUserNodeCount] = useState<number | null>(() =>
    chartId ? (userNodeCountByChartId.get(chartId) ?? null) : null
  );
  const numOfNodes = userNodeCount ?? defaultNodeCount;
  const showNodeCountSelect = useThreeTier || !isFixedCount;
  const effectiveNumOfNodes = showNodeCountSelect
    ? numOfNodes
    : (topNFilterValue ?? 10);

  const handleNodeCountChange = (value: number) => {
    setUserNodeCount(value);
    if (chartId) userNodeCountByChartId.set(chartId, value);
  };

  const sankeyData = buildSankeyData({
    data: primaryEntries,
    useThreeTier,
    metric,
    effectiveNumOfNodes,
    color,
    getPlotColor,
  });

  const sankeyOptions = item?.plot?.sankey;
  const leftFlowLabel =
    sankeyOptions?.leftFlowLabel ??
    (useThreeTier ? THREE_TIER_LEFT_FLOW_LABEL : undefined);
  const rightFlowLabel =
    sankeyOptions?.rightFlowLabel ??
    (useThreeTier ? THREE_TIER_RIGHT_FLOW_LABEL : undefined);

  if (!sankeyData) return null;

  return (
    <div className={styles.sankeyRoot}>
      <div
        className={classNames(
          styles.sankeyBody,
          showNodeCountSelect && styles.sankeyBodyWithFooter
        )}
      >
        <SharableChart
          Component={SankeyChart}
          data={sankeyData}
          color={color}
          showLinkValues={showLinkValues}
          scale={scale}
          showOrderInversions={showOrderInversions}
          inversionTolerance={noticeableDifference / 100}
          leftFlowLabel={leftFlowLabel}
          rightFlowLabel={rightFlowLabel}
        />
      </div>
      {showNodeCountSelect ? (
        <SankeyFooter
          numOfNodes={numOfNodes}
          onNumOfNodesChange={handleNodeCountChange}
          options={nodeCountOptions}
        />
      ) : null}
    </div>
  );
};
